import * as go from 'gojs';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { MAX_COMPETENCY_QUESTIONS } from 'src/constants/limits';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import { ROUTE } from 'src/constants/routes';
import {
  CompetencyQuestion,
  EvaluationSummaryIssue,
} from 'src/constants/voicebox';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Resource } from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isModelConceptClass,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { useProjectSidebarToggle } from 'src/containers/ProjectSidebar/hooks/useProjectSidebarToggle';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';
import { useEditQuestions } from 'src/utils/voicebox/hooks/useEditQuestions';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

export type SummaryAction = {
  isDisabled?: boolean;
  isLoading?: boolean;
  label: string;
  onClick: () => Promise<void>;
};

export const useUnansweredQuestion = (
  issue: EvaluationSummaryIssue
): SummaryAction => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const { evaluateQuestions, isLoadingEvaluations, loadingEvaluationIds } =
    useEvaluateQuestions();

  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );
  const question = questions.find((q) => q.id === issue.target);

  const isLoading = loadingEvaluationIds.has(issue.target);
  const isDisabled = !question || isLoadingEvaluations;

  const onClick = React.useCallback(async () => {
    if (!question) {
      return;
    }

    let updatedQuestion: CompetencyQuestion = question;
    if (!question.evaluation?.query) {
      const updatedQuestions = await evaluateQuestions([question], {
        waitForQueries: true,
      });
      updatedQuestion =
        updatedQuestions?.find((q) => q.id === question.id) || question;
    }

    if (updatedQuestion.evaluation) {
      dispatch(uiSlice.actions.toggledVoiceboxDrawerSummary({ isOpen: false }));
      dispatch(
        projectsActionCreators.openedProjectCompetencyQuestion({
          id: projectId,
          question: updatedQuestion,
        })
      );
    }
  }, [dispatch, evaluateQuestions, projectId, question]);

  return {
    isDisabled,
    isLoading,
    label: issue.label,
    onClick,
  };
};

export const useUnusedDataset = (
  issue: EvaluationSummaryIssue
): SummaryAction => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const { projectId } = useRouterParams();
  const isMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const nodeData = diagram?.model.nodeDataArray.find(
    (data): data is Resource => isResource(data) && data.value === issue.target
  );

  const isDisabled = !nodeData;

  const onClick = React.useCallback(async () => {
    if (!diagram || !nodeData) {
      return;
    }

    // Highlighting the node on the canvas is diagram-only; the drawer still
    // opens (and navigates) headless.
    if (!diagram.headless) {
      const keysToSelect: go.Set<go.Part> = new go.Set();
      diagram.nodes.each((item) => {
        if (item.data.value === nodeData.value) {
          keysToSelect.add(item);
        }
      });

      commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
        setDiagramSelection(keysToSelect)(diagram)
      );
    }

    dispatch(
      uiSlice.actions.changedEntityDrawer({
        projectId,
        drawer: {
          entityValue: nodeData.value,
          type: EntityDrawerType.RESOURCE,
        },
      })
    );

    if (isMappingRoute) {
      dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isPendingOpen: true }));
      dispatch(
        uiSlice.actions.toggledVoiceboxDrawerSummary({ isPendingOpen: true })
      );
      navigate(getProjectRoute(projectId));
    }
  }, [diagram, dispatch, isMappingRoute, navigate, nodeData, projectId]);

  return {
    isDisabled,
    label: nodeData?.text || issue.label,
    onClick,
  };
};

export const useUnlinkedConcept = (
  issue: EvaluationSummaryIssue
): SummaryAction => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const { projectId } = useRouterParams();
  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const isMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const nodeData = diagram?.model.nodeDataArray.find(
    (data) => isModelConceptClass(data) && data.value === issue.target
  );

  const isDisabled = !nodeData;

  const onClick = React.useCallback(async () => {
    if (!diagram || !nodeData) {
      return;
    }

    // Highlighting the node on the canvas is diagram-only; the drawer still
    // opens (and navigates) headless.
    if (!diagram.headless) {
      const keysToSelect: go.Set<go.Part> = new go.Set();
      diagram.nodes.each((item) => {
        if (item.data.value === nodeData.value) {
          keysToSelect.add(item);
        }
      });

      commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
        setDiagramSelection(keysToSelect)(diagram)
      );
    }

    dispatch(
      uiSlice.actions.changedEntityDrawer({
        projectId,
        drawer: {
          entityValue: nodeData.value,
          type: EntityDrawerType.CLASS,
        },
      })
    );

    if (isMappingRoute) {
      dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isPendingOpen: true }));
      dispatch(
        uiSlice.actions.toggledVoiceboxDrawerSummary({ isPendingOpen: true })
      );
      navigate(getProjectRoute(projectId));
    }
  }, [diagram, dispatch, isMappingRoute, navigate, nodeData, projectId]);

  return {
    isDisabled,
    label: getLabelForIri(labelsByIri, issue.target),
    onClick,
  };
};

export const useAddQuestions = (): SummaryAction => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );
  const isDisabled = questions.length >= MAX_COMPETENCY_QUESTIONS;

  const toggleSidebar = useProjectSidebarToggle();
  const { onEditQuestion } = useEditQuestions();

  const onClick = React.useCallback(async () => {
    onEditQuestion(questions.length, '');
    toggleSidebar((prev) => ({
      ...prev,
      [ProjectSidebarTileId.MAPPINGS]: true,
      [ProjectSidebarTileId.COMPETENCY_QUESTIONS]: false,
    }));
    dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isOpen: false }));
  }, [dispatch, onEditQuestion, questions.length, toggleSidebar]);

  return {
    isDisabled,
    label: '',
    onClick,
  };
};

export const useAddDataset = (): SummaryAction => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();

  const isDisabled = useReduxSelector(isDraftDiagramSelector(projectId));

  const onClick = React.useCallback(async () => {
    dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: true }));
  }, [dispatch]);

  return {
    isDisabled,
    label: '',
    onClick,
  };
};
